/**
 * Clock In's own palette, icon and injected style block, shared by
 * ClockInScreen and the pages reached from it (the own-shift edit and the
 * missed shift). A module of its own so those pages do not import the screen:
 * a suite that mocks ClockInScreen would otherwise hand them an undefined
 * palette at import time.
 */
import React from 'react';

// ─── Design tokens ───────────────────────────────────────────────
// Every colour is a --ci-* custom property, defined in the injected style
// block below: its DARK value is the colour this screen has always drawn, so
// dark renders exactly as before, and its LIGHT value is a theme token. The
// hex values used to be inlined here, which put dark-theme ink on the light
// theme's grey page ("Not clocked in" and Shop/Field were white on grey).
//
// A hue WORD and a hue MARK are separate keys on purpose. Amber, green and red
// words sit on the page grey, where no amber/green/red ink clears 4.5 in light
// (--fieldiq-accent-signal reads 4.36 there), so in light a word takes neutral
// ink and the icon, dot, rail or pill beside it carries the hue.
export const C = {
  bg:                    'var(--ci-bg)',
  surface:               'var(--ci-surface)',
  surfaceLow:            'var(--ci-bg)',
  surfaceHigh:           'var(--ci-surface)',
  surfaceHighest:        'hsl(var(--secondary))',
  primary:               'var(--ci-amber)',        // amber mark: icon, dot, rail, glow
  primaryInk:            'var(--ci-amber-ink)',    // amber word
  secondary:             'var(--ci-green)',        // green mark
  secondaryInk:          'var(--ci-green-ink)',    // green word
  secondaryContainer:    'var(--ci-go)',
  onSecondaryContainer:  'var(--ci-on-go)',
  onSurface:             'var(--ci-ink)',
  onSurfaceVariant:      'var(--ci-ink-2)',
  outlineVariant:        'var(--ci-outline)',
  error:                 'var(--ci-red)',          // red mark
  errorInk:              'var(--ci-red-ink)',      // red word
} as const;

// A colour at a fixed opacity. Replaces the hex-alpha suffix (`${C.x}40`),
// which only works while C holds hex; the percentages are those suffixes
// exactly (0x40 / 255 = 25.098%), so dark draws the same pixels.
export const mix = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

// ─── Icon helper ─────────────────────────────────────────────────
export function MI({ name, className = '', filled = false, size = 24, style = {} }: { name: string; className?: string; filled?: boolean; size?: number; style?: React.CSSProperties }) {
  return (
    <span
      className={`material-symbols-outlined ${className}`}
      style={{ fontSize: size, fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0", ...style }}
    >
      {name}
    </span>
  );
}

// ─── Injected keyframes ──────────────────────────────────────────
const STYLE_ID = 'clockin-stitch-style';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes ci-scale-in { 0%{transform:scale(0.5);opacity:0} 100%{transform:scale(1);opacity:1} }
    @keyframes ci-pulse-dot { 0%,100%{opacity:1} 50%{opacity:.4} }
    @keyframes ci-ring-rotate { 0%{transform:rotate(-90deg)} 100%{transform:rotate(270deg)} }
    @keyframes ci-slide-up { 0%{transform:translateY(100%)} 100%{transform:translateY(0)} }
    @keyframes ci-slide-down { 0%{transform:translateY(0)} 100%{transform:translateY(100%)} }
    .ci-scale-in { animation: ci-scale-in .4s cubic-bezier(.16,1,.3,1) forwards }
    .ci-pulse-dot { animation: ci-pulse-dot 2s ease-in-out infinite }
    .ci-sheet-enter { animation: ci-slide-up .35s cubic-bezier(.32,.72,0,1) forwards }
    .ci-sheet-exit  { animation: ci-slide-down .25s ease-in forwards }
    .font-headline { font-family: 'Space Grotesk', sans-serif }
    .font-body     { font-family: 'Inter', sans-serif }
    :root {
      --ci-scheme: dark;
      --ci-bg: hsl(var(--background));
      --ci-surface: hsl(var(--card));
      --ci-ink: #f1f3fc;
      --ci-ink-2: #a8abb3;
      --ci-outline: #44484f;
      --ci-edge-13: color-mix(in srgb, #44484f 12.549%, transparent);
      --ci-edge-19: color-mix(in srgb, #44484f 18.824%, transparent);
      --ci-edge-25: color-mix(in srgb, #44484f 25.098%, transparent);
      --ci-edge-30: color-mix(in srgb, #44484f 30.196%, transparent);
      --ci-edge-38: color-mix(in srgb, #44484f 37.647%, transparent);
      --ci-hair-05: rgba(255,255,255,0.05);
      --ci-hair-06: rgba(255,255,255,0.06);
      --ci-hair-10: rgba(255,255,255,0.1);
      --ci-hair-20: rgba(255,255,255,0.2);
      --ci-row-on: rgba(255,255,255,0.05);
      --ci-placeholder: rgba(255,255,255,0.3);
      --ci-amber: #ffc965;
      --ci-amber-ink: #ffc965;
      --ci-green: #96f7af;
      --ci-green-ink: #96f7af;
      --ci-red: #ff7351;
      --ci-red-ink: #ff7351;
      --ci-red-wash: rgba(255,115,81,0.1);
      --ci-go: #006d37;
      --ci-on-go: #e3ffe4;
      --ci-go-fill: linear-gradient(135deg, #006d37, #00522a);
      --ci-confirm: #96f7af;
      --ci-on-confirm: hsl(var(--primary-foreground));
      --ci-active-pill: color-mix(in srgb, #96f7af 7.059%, transparent);
      --ci-active-pill-edge: color-mix(in srgb, #96f7af 18.824%, transparent);
      --ci-active-pill-ink: #96f7af;
      --ci-standby-pill: color-mix(in srgb, #ffc965 8.235%, transparent);
      --ci-standby-pill-edge: color-mix(in srgb, #ffc965 18.824%, transparent);
      --ci-standby-pill-ink: #ffc965;
      --ci-pill-ok: rgba(150,247,175,0.1);
      --ci-pill-ok-ink: #96f7af;
      --ci-pill-bad: rgba(255,115,81,0.1);
      --ci-pill-bad-ink: #ff7351;
      --ci-pill-wait: rgba(255,201,101,0.1);
      --ci-pill-wait-ink: #ffc965;
      --ci-pill-neutral: var(--ci-hair-10);
      --ci-pill-neutral-ink: var(--ci-ink-2);
      --ci-toast-bad: #ff7351;
      /* Dark ink, not white: #fff on #ff7351 is 2.68:1; --fi-signal-ink
         (#11182a) on it is 6.59:1, and is what light mode already uses. */
      --ci-on-toast-bad: var(--fi-signal-ink);
      --ci-nojob-dot: #2dd4bf;
    }
    :root[data-theme="light"] {
      --ci-scheme: light;
      --ci-bg: var(--fieldiq-dark);
      --ci-surface: var(--fieldiq-card);
      --ci-ink: var(--fieldiq-heading);
      --ci-ink-2: var(--fieldiq-text);
      --ci-outline: var(--fieldiq-border);
      --ci-edge-13: var(--fieldiq-border);
      --ci-edge-19: var(--fieldiq-border);
      --ci-edge-25: var(--fieldiq-border);
      --ci-edge-30: var(--fieldiq-border);
      --ci-edge-38: var(--fieldiq-border);
      --ci-hair-05: var(--fieldiq-border);
      --ci-hair-06: var(--fieldiq-border);
      --ci-hair-10: var(--fieldiq-border);
      --ci-hair-20: var(--fieldiq-border);
      --ci-row-on: color-mix(in srgb, var(--fieldiq-heading) 5%, transparent);
      --ci-placeholder: var(--fieldiq-muted);
      --ci-amber: var(--fieldiq-accent);
      --ci-amber-ink: var(--fieldiq-heading);
      --ci-green: var(--fieldiq-success);
      --ci-green-ink: var(--fieldiq-heading);
      --ci-red: var(--fieldiq-danger);
      --ci-red-ink: var(--fieldiq-heading);
      --ci-red-wash: color-mix(in srgb, var(--fieldiq-danger) 10%, transparent);
      --ci-go: var(--fi-signal-green);
      --ci-on-go: var(--fi-signal-ink);
      --ci-go-fill: var(--fi-signal-green);
      --ci-confirm: var(--fi-signal-green);
      --ci-on-confirm: var(--fi-signal-ink);
      --ci-active-pill: var(--fi-signal-green);
      --ci-active-pill-edge: rgb(0 0 0 / 0.2);
      --ci-active-pill-ink: var(--fi-signal-ink);
      --ci-standby-pill: var(--fi-signal-amber);
      --ci-standby-pill-edge: rgb(0 0 0 / 0.2);
      --ci-standby-pill-ink: var(--fi-signal-ink);
      --ci-pill-ok: var(--fi-signal-green);
      --ci-pill-ok-ink: var(--fi-signal-ink);
      --ci-pill-bad: var(--fi-signal-red);
      --ci-pill-bad-ink: var(--fi-signal-ink);
      --ci-pill-wait: var(--fi-signal-amber);
      --ci-pill-wait-ink: var(--fi-signal-ink);
      --ci-pill-neutral: var(--ci-hair-10);
      --ci-pill-neutral-ink: var(--ci-ink-2);
      --ci-toast-bad: var(--fi-signal-red);
      --ci-on-toast-bad: var(--fi-signal-ink);
      --ci-nojob-dot: var(--fi-signal-slate);
    }
    .ci-dark-override input,
    .ci-dark-override select,
    .ci-dark-override textarea {
      background-color: ${C.bg} !important;
      color: ${C.onSurface} !important;
      border-color: var(--ci-edge-25) !important;
    }
    .ci-dark-override textarea::placeholder { color: var(--ci-placeholder); }
    .ci-dark-override select option {
      background-color: ${C.surface} !important;
      color: ${C.onSurface} !important;
    }
  `;
  document.head.appendChild(s);
}

